import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/Brand";
import { toast } from "sonner";
import { FileSpreadsheet, Send, CheckCircle2, ArrowRightCircle, Info } from "lucide-react";

const INV_STYLES = { draft: "badge-draft", submitted: "badge-submitted", approved: "badge-approved", transferred: "badge-reviewed" };

function InvoiceCard({ inv, isAdmin, onChange }) {
  const [open, setOpen] = useState(false);
  const [po, setPo] = useState(inv.po_number || "");
  const [notes, setNotes] = useState(inv.notes || "");
  const [lines, setLines] = useState(inv.line_items.map((li) => ({ ...li })));
  const [busy, setBusy] = useState(false);
  const editable = inv.status === "draft";

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/invoices/${inv.id}`, {
        po_number: po, notes,
        line_items: lines.map((li) => ({ log_id: li.log_id, billing_unit: li.billing_unit, quantity: Number(li.quantity) })),
      });
      onChange(); toast.success("Invoice draft saved.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const act = async (action, msg) => {
    setBusy(true);
    try { await api.post(`/invoices/${inv.id}/${action}`); onChange(); toast.success(msg); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card p-5 space-y-3" data-testid={`invoice-card-${inv.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button className="text-left" onClick={() => setOpen(!open)} data-testid={`invoice-toggle-${inv.id}`}>
          <div className="font-bold text-navy">{inv.group_label} <span className="text-xs font-normal text-[#78716C]">({inv.group_type === "school" ? "school invoice" : "non-school project"})</span></div>
          <div className="text-xs text-[#78716C]">{inv.period} · {inv.consultant_name} · {inv.billable_days} billable day{inv.billable_days === 1 ? "" : "s"}</div>
        </button>
        <div className="flex items-center gap-2">
          <span className="text-sm font-black text-navy" data-testid={`invoice-amount-${inv.id}`}>
            {inv.amount != null ? `$${inv.amount.toLocaleString()}` : "Rate not set"}</span>
          <span className={INV_STYLES[inv.status] || "badge-neutral"} data-testid={`invoice-status-${inv.id}`}>{inv.status}</span>
        </div>
      </div>
      {inv.rate_note && <p className="text-xs text-[#92400E]" data-testid={`invoice-ratenote-${inv.id}`}>{inv.rate_note}</p>}
      {open && (
        <div className="border-t border-[#F3EFE6] pt-3 space-y-3 fade-up">
          <table className="w-full">
            <thead><tr><th className="table-th">Service date</th><th className="table-th">Services</th>
              <th className="table-th">Billing unit</th><th className="table-th">Qty</th></tr></thead>
            <tbody>
              {lines.map((li, i) => (
                <tr key={li.log_id} data-testid={`invoice-line-${li.log_id}`}>
                  <td className="table-td font-bold">
                    {li.date}
                    {li.split_day && <span className="badge-draft ml-2" data-testid={`split-day-${li.log_id}`}>Split day — verify total across schools ≤ agreed amount</span>}
                  </td>
                  <td className="table-td text-xs">{(li.service_types || []).join(" · ")}</td>
                  <td className="table-td">
                    {editable ? (
                      <select className="input !py-1 !px-2 text-xs w-32" value={li.billing_unit}
                        data-testid={`invoice-unit-${li.log_id}`}
                        onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, billing_unit: e.target.value, quantity: e.target.value === "half_day" ? 1 : x.quantity } : x)))}>
                        <option value="day">Full day</option>
                        <option value="half_day">Half day</option>
                        <option value="hour">Hourly</option>
                      </select>
                    ) : <span className="text-xs">{li.billing_unit === "half_day" ? "Half day" : li.billing_unit === "hour" ? "Hourly" : "Full day"}</span>}
                  </td>
                  <td className="table-td">
                    {editable ? (
                      <input type="number" min="0.5" max="24" step="0.5" className="input !py-1 !px-2 text-xs w-20"
                        value={li.quantity} data-testid={`invoice-qty-${li.log_id}`}
                        onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))} />
                    ) : <span className="text-xs">{li.quantity}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-[#78716C]">Every line defaults to one full day — the system never assumes a full billable day without your confirmation. Multiple sessions on the same date and school stay a single line.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="label">Purchase order # {inv.po_required && <span className="text-[#991B1B]">(required by engagement)</span>}</label>
              <input className="input" value={po} disabled={!editable} data-testid={`invoice-po-${inv.id}`}
                onChange={(e) => setPo(e.target.value)} placeholder={inv.po_required ? "Required before submission" : "Optional"} /></div>
            <div><label className="label">Notes</label>
              <input className="input" value={notes} disabled={!editable} data-testid={`invoice-notes-${inv.id}`}
                onChange={(e) => setNotes(e.target.value)} /></div>
          </div>
          <div className="flex flex-wrap gap-2">
            {editable && <button className="btn-outline !py-1.5" onClick={save} disabled={busy} data-testid={`invoice-save-${inv.id}`}>Save</button>}
            {editable && <button className="btn-primary !py-1.5" onClick={() => act("submit", "Submitted to CKingEducation for review. Nothing was sent externally.")} disabled={busy} data-testid={`invoice-submit-${inv.id}`}>
              <Send size={14} /> Submit to CKingEducation</button>}
            {isAdmin && inv.status === "submitted" && (
              <button className="btn-primary !py-1.5" onClick={() => act("approve", "Invoice approved. No payment initiated.")} disabled={busy} data-testid={`invoice-approve-${inv.id}`}>
                <CheckCircle2 size={14} /> Approve</button>)}
            {isAdmin && inv.status === "approved" && (
              <button className="btn-outline !py-1.5" onClick={() => act("mark-transferred", "Recorded as a manual transfer to accounting. No QuickBooks sync occurred and no payment was initiated.")} disabled={busy} data-testid={`invoice-transfer-${inv.id}`}>
                <ArrowRightCircle size={14} /> Record manual transfer (not a QuickBooks sync)</button>)}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Invoices() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [invoices, setInvoices] = useState(null);
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().slice(0, 8) + "01");
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [consultants, setConsultants] = useState([]);
  const [forConsultant, setForConsultant] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data));
  useEffect(() => {
    load();
    if (isAdmin) api.get("/admin/consultants").then((r) => setConsultants(r.data));
  }, []);

  const prepare = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/prepare", { date_from: dateFrom, date_to: dateTo, consultant_id: forConsultant || undefined });
      toast.success(`${data.created.length} invoice draft(s) prepared.${data.skipped_days_already_invoiced.length ? ` Skipped already-invoiced days: ${data.skipped_days_already_invoiced.join(", ")}` : ""}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!invoices) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="invoices-loading">Loading invoices…</div>;

  return (
    <div className="max-w-5xl space-y-6" data-testid="invoices-page">
      <div className="fade-up">
        <p className="eyebrow">Consultant invoices → CKingEducation</p>
        <h1 className="section-title text-3xl mt-1">Invoice Drafts</h1>
        <p className="mt-3 text-sm text-[#57534E] max-w-3xl">
          Approved service records → editable invoice draft → consultant review & submit → CKingEducation
          approval → transfer to accounting. Invoices stay internal until Admin configures a destination in
          Settings. No payments are initiated and QuickBooks is not connected yet.
        </p>
      </div>

      <div className="notice-privacy fade-up-1" data-testid="invoice-routing-note">
        <Info size={16} className="shrink-0 mt-0.5" />
        <span>Routing is currently <b>internal drafts only</b>. Your existing submission process still applies —
          this hub prepares drafts; it does not submit them anywhere. One invoice per school (or non-school
          project) per month; the same service day can never be billed twice.</span>
      </div>

      <div className="card p-5 flex flex-wrap items-end gap-3 fade-up-1" data-testid="prepare-invoice-card">
        <div><label className="label">From date</label>
          <input type="date" className="input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="invoice-date-from" /></div>
        <div><label className="label">To date</label>
          <input type="date" className="input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="invoice-date-to" /></div>
        {isAdmin && (
          <div><label className="label">Consultant (blank = me)</label>
            <select className="input" value={forConsultant} onChange={(e) => setForConsultant(e.target.value)} data-testid="invoice-consultant-select">
              <option value="">Myself</option>
              {consultants.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></div>
        )}
        <button className="btn-primary" onClick={prepare} disabled={busy} data-testid="prepare-invoice-button">
          <FileSpreadsheet size={15} /> {busy ? "Preparing…" : "Prepare invoice drafts from approved records"}</button>
      </div>

      <div className="space-y-4 fade-up-2" data-testid="invoice-list">
        {invoices.map((inv) => <InvoiceCard key={inv.id} inv={inv} isAdmin={isAdmin} onChange={load} />)}
        {invoices.length === 0 && (
          <p className="text-sm text-[#78716C]" data-testid="invoices-empty">No invoice drafts yet. Approve daily records first, then prepare drafts above.</p>)}
      </div>
    </div>
  );
}
